// File: client/src/components/dashboard/MatrizDashboard.js
import React, { useState } from 'react';
import useAlert from '../../hooks/useAlert';
import { useMatrizDashboard, useContaDashboard } from '../../hooks/useReports'; // ✅ NOVO HOOK

// Componentes
import PageHeader from '../layout/PageHeader';
import KpiCard from './KpiCard';

// Ícones
import { Building, DollarSign, UserCheck, UserPlus, UserX, AlertTriangle, Filter as FilterIcon, CalendarDays, TrendingUp, Users } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getAndRegisterDatePickerLocale } from '../../utils/dateLocale';

const MatrizDashboard = () => {
    const { setAlert } = useAlert();
    
    // 🔥 ESTADO DE FILTROS
    const [filters, setFilters] = useState({
        startDate: new Date(new Date().setDate(new Date().getDate() - 29)),
        endDate: new Date()
    });
    
    const [showFilters, setShowFilters] = useState(false);

    // 🔥 USANDO NOVO HOOK - useMatrizDashboard
    const {
        data: dashboardData,
        isLoading
    } = useMatrizDashboard(filters);

    // ✅ NOVO: dados operacionais (agendamentos/faturamento/clientes) de
    // TODAS as contas e franquias somadas — antes o dashboard da matriz só
    // mostrava métricas de assinatura, nunca dava pra ver o volume real de
    // operação da plataforma. getContaDashboard, sem account/franquia no
    // usuário logado, agora agrega globalmente (ver reportsController.js).
    const {
        data: operacaoData,
        isLoading: isLoadingOperacao
    } = useContaDashboard(filters);

    // 🔥 MANIPULADOR DE MUDANÇAS DE FILTRO
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // 🔥 CONFIGURAÇÕES DE LOCALIDADE
    const datePickerLocale = getAndRegisterDatePickerLocale('pt-BR');

    // 🔥 AÇÕES DA PÁGINA
    const pageActions = (
        <button 
            onClick={() => setShowFilters(prevState => !prevState)} 
            className="action-button action-button-secondary"
        >
            <FilterIcon size={18} className="icon"/> 
            {showFilters ? 'Ocultar Filtros' : 'Exibir Filtros'}
        </button>
    );

    return (
        <div className="page-management-container">
            <PageHeader
                icon={<Building size={24} />}
                title="Dashboard da Plataforma"
                actions={pageActions}
            />

            {/* 🔥 SEÇÃO DE FILTROS */}
            {showFilters && (
                <div className="content-panel">
                    <h3 className="panel-title">Filtros de Período</h3>
                    <div className="form-grid-container">
                        <div className="form-group">
                            <label>Data Inicial</label>
                            <DatePicker 
                                selected={filters.startDate} 
                                onChange={date => handleFilterChange('startDate', date)} 
                                locale={datePickerLocale} 
                                dateFormat="dd/MM/yyyy" 
                                className="form-input" 
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>Data Final</label>
                            <DatePicker 
                                selected={filters.endDate} 
                                onChange={date => handleFilterChange('endDate', date)} 
                                locale={datePickerLocale} 
                                dateFormat="dd/MM/yyyy" 
                                className="form-input" 
                            />
                        </div>
                    </div>
                </div>
            )}
            
            <div className="dashboard-grid" style={{ marginTop: '1.5rem' }}>
                <KpiCard 
                    icon={DollarSign} 
                    title="Receita Recorrente Mensal (MRR)" 
                    value={dashboardData?.totalMRR?.toFixed(2) || '0.00'} 
                    prefix="R$ " 
                    isLoading={isLoading} 
                />
                <KpiCard 
                    icon={UserCheck} 
                    title="Assinaturas Ativas" 
                    value={dashboardData?.ativas || 0} 
                    isLoading={isLoading} 
                />
                <KpiCard 
                    icon={UserPlus} 
                    title="Novas Contas (Período)" 
                    value={dashboardData?.novasContas || 0} 
                    isLoading={isLoading} 
                />
                <KpiCard 
                    icon={UserCheck} 
                    title="Contas em Trial" 
                    value={dashboardData?.trial || 0} 
                    isLoading={isLoading} 
                />
                <KpiCard 
                    icon={UserX} 
                    title="Assinaturas Canceladas" 
                    value={dashboardData?.canceladas || 0} 
                    isLoading={isLoading} 
                />
                <KpiCard
                    icon={AlertTriangle}
                    title="Assinaturas Inadimplentes"
                    value={dashboardData?.inadimplentes || 0}
                    isLoading={isLoading}
                />
            </div>

            {/* ✅ NOVO: Operação da Plataforma (soma de todas as contas/franquias) */}
            <h4 className="panel-title" style={{ marginTop: '1.5rem' }}>Operação da Plataforma (Todas as Contas)</h4>
            <div className="dashboard-grid" style={{ marginTop: '0.5rem' }}>
                <KpiCard
                    icon={DollarSign}
                    title="Faturamento (Período)"
                    value={operacaoData?.kpis?.faturamentoTotal?.toFixed(2) || '0.00'}
                    prefix="R$ "
                    isLoading={isLoadingOperacao}
                />
                <KpiCard
                    icon={TrendingUp}
                    title="Ticket Médio (Período)"
                    value={operacaoData?.kpis?.ticketMedio?.toFixed(2) || '0.00'}
                    prefix="R$ "
                    isLoading={isLoadingOperacao}
                />
                <KpiCard
                    icon={CalendarDays}
                    title="Agendamentos (Período)"
                    value={operacaoData?.kpis?.totalAgendamentos || 0}
                    isLoading={isLoadingOperacao}
                />
                <KpiCard
                    icon={Users}
                    title="Novos Clientes (Período)"
                    value={operacaoData?.kpis?.novosClientes || 0}
                    isLoading={isLoadingOperacao}
                />
            </div>

            {/* 🔥 INFORMAÇÕES ADICIONAIS */}
            {dashboardData && (
                <div className="content-panel" style={{ marginTop: '1.5rem' }}>
                    <h4 className="panel-title">Resumo do Período</h4>
                    <div className="row">
                        <div className="col-md-6">
                            <p><strong>Período analisado:</strong> {filters.startDate.toLocaleDateString('pt-BR')} até {filters.endDate.toLocaleDateString('pt-BR')}</p>
                            <p><strong>Total de assinaturas:</strong> {dashboardData.totalAssinaturas || 0}</p>
                        </div>
                        <div className="col-md-6">
                            <p><strong>Contas ativas:</strong> {dashboardData.ativas || 0}</p>
                            <p><strong>Contas em trial:</strong> {dashboardData.trial || 0}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MatrizDashboard;